import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TemplatePageCategoryRequestOptions } from '@models/types'

const watchEntityKeys = ['templatePageCategories']

export type TemplatePageCategoriesFilters = {
  brand_id?: number,
  global?: 'true' | 'false',
  hidden?: 'true' | 'false',
  string?: string,
}

interface UseEntitiesOptions {
  entityKey?: string,
  filters?: TemplatePageCategoriesFilters,
  performHttpRequests?: boolean,
  requestOptions?: TemplatePageCategoryRequestOptions,
}

function useTemplatePageCategories(options: UseEntitiesOptions = {}) {
  const { filters = {}, requestOptions } = options
  const {
    brand_id: filterBrandId,
    global: filterGlobal,
    hidden: filterHidden,
    string: filterString,
  } = filters

  const {
    updatedEntities: { templatePageCategories: templatePageCategoriesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const templatePageCategories = useSelector(reduxState => reduxState.entities.templatePageCategories)

  // Filter redux entities with provided filters
  const filteredTemplatePageCategories = useMemo(() => {
    const filtered = Object.values(templatePageCategories).filter((templatePageCategory) => {
      const {
        brand_id, global, hidden, id, title,
      } = templatePageCategory

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchBrand = filterBrandId ? !global && matchFilterNumber(brand_id, filterBrandId) : true
      const matchGlobal = filterGlobal ? global.toString() === filterGlobal : true
      const matchHidden = filterHidden ? hidden.toString() === filterHidden : true

      return matchBrand && matchGlobal && matchHidden && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [templatePageCategoriesUpdatedAt, JSON.stringify(filters)])

  const filteredTemplatePageCategoriesCount = filteredTemplatePageCategories.length
  const hasFilteredTemplatePageCategories = !!filteredTemplatePageCategoriesCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTemplatePageCategoriesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const { loading: loadingTemplatePageCategories } = useReduxAction(
    'templatePageCategories',
    'loadTemplatePageCategories',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTemplatePageCategories,
    filteredTemplatePageCategoriesCount,
    hasFilteredTemplatePageCategories,
    loading: loadingTemplatePageCategories,
  }
}

export default useTemplatePageCategories
